import type { Meeting, NormalizedSection } from "../shared/schemas";

export type UtilizationTier =
  | "critical-under-utilization"
  | "below-optimal"
  | "optimal"
  | "near-capacity"
  | "over-capacity";

export interface Booking {
  key: string;
  termCode: string;
  campus: string;
  room: string;
  dayCode: string;
  startMinutes: number;
  endMinutes: number;
  startDate: string | null;
  endDate: string | null;
  crossListGroup: string;
  sectionCrns: string[];
  totalMaxEnrollment: number;
  roomCapacity: number;
  utilization: number;
}

export interface Conflict {
  bookingA: Booking;
  bookingB: Booking;
  overlapStart: number;
  overlapEnd: number;
}

export interface SectionDelta {
  crn: string;
  kind: "added" | "removed" | "changed" | "unchanged";
  before: NormalizedSection | null;
  after: NormalizedSection | null;
  changedFields: string[];
}

function scalarString(value: unknown): string {
  if (value == null) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  return "";
}

export function parseInteger(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  const parsed = Number.parseInt(scalarString(value).replace(/,/g, "").trim(), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function parseDecimal(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const normalized = scalarString(value)
    .replace(/,/g, "")
    .replace(/%$/, "")
    .trim();
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return scalarString(value).trim().endsWith("%") ? parsed / 100 : parsed;
}

export function parseTimeToMinutes(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number" && value >= 0 && value < 1) {
    return Math.round(value * 24 * 60);
  }
  const raw = scalarString(value).trim().toUpperCase();
  const meridiem = raw.endsWith("AM") ? "AM" : raw.endsWith("PM") ? "PM" : null;
  const numeric = raw.replace(/[^\d:]/g, "");
  let hours: number;
  let minutes: number;
  if (numeric.includes(":")) {
    const [hourText, minuteText = "0"] = numeric.split(":");
    hours = Number.parseInt(hourText, 10);
    minutes = Number.parseInt(minuteText, 10);
  } else {
    const padded = numeric.padStart(4, "0");
    hours = Number.parseInt(padded.slice(0, -2), 10);
    minutes = Number.parseInt(padded.slice(-2), 10);
  }
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) {
    return null;
  }
  if (meridiem === "PM" && hours < 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  if (hours > 23) return null;
  return hours * 60 + minutes;
}

export function parseDurationToMinutes(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1 ? Math.round(value * 24 * 60) : Math.round(value);
  }
  const raw = scalarString(value).trim();
  if (/^\d+:\d{1,2}$/.test(raw)) {
    const [hours, minutes] = raw.split(":").map(Number);
    return hours * 60 + minutes;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeDayCodes(value: unknown): Meeting["dayCode"][] {
  const raw = scalarString(value)
    .trim()
    .toUpperCase()
    .replace(/TH/g, "R")
    .replace(/TU/g, "T")
    .replace(/SU/g, "U")
    .replace(/[^MTWRFSU]/g, "");
  return [...new Set(raw.split(""))].filter((day): day is Meeting["dayCode"] =>
    ["M", "T", "W", "R", "F", "S", "U"].includes(day),
  );
}

export function calculateSeatsAvailable(maxEnrollment: number, actualEnrollment: number): number {
  return maxEnrollment - actualEnrollment;
}

export function calculateWaitlistPressure(waitlistEnrollment: number, waitlistMax: number): number {
  if (waitlistEnrollment <= 0) return 0;
  if (waitlistMax <= 0) return 1;
  return waitlistEnrollment / waitlistMax;
}

export function calculateUtilization(maxEnrollment: number, roomCapacity: number): number {
  return roomCapacity > 0 ? maxEnrollment / roomCapacity : 0;
}

export function classifyUtilization(rate: number): UtilizationTier {
  if (rate > 1) return "over-capacity";
  if (rate >= 0.96) return "near-capacity";
  if (rate >= 0.7) return "optimal";
  if (rate >= 0.4) return "below-optimal";
  return "critical-under-utilization";
}

function dateRangesOverlap(
  startA: string | null,
  endA: string | null,
  startB: string | null,
  endB: string | null,
): boolean {
  const normalizedStartA = startA ?? "0000-01-01";
  const normalizedEndA = endA ?? "9999-12-31";
  const normalizedStartB = startB ?? "0000-01-01";
  const normalizedEndB = endB ?? "9999-12-31";
  return normalizedStartA <= normalizedEndB && normalizedStartB <= normalizedEndA;
}

export function buildBookings(sections: NormalizedSection[]): Booking[] {
  const bookings = new Map<string, Booking>();
  for (const section of sections) {
    for (const meeting of section.meetings) {
      const groupKey = section.crossListGroup
        ? `cross:${section.termCode}:${section.crossListGroup}:${meeting.campus}:${meeting.room}:${meeting.dayCode}:${meeting.startMinutes}:${meeting.endMinutes}`
        : `section:${section.termCode}:${section.crn}:${meeting.dayCode}:${meeting.startMinutes}:${meeting.endMinutes}:${meeting.room}`;
      const existing = bookings.get(groupKey);
      if (existing) {
        if (!existing.sectionCrns.includes(section.crn)) {
          existing.sectionCrns.push(section.crn);
          existing.totalMaxEnrollment += section.maxEnrollment;
          existing.roomCapacity = Math.max(existing.roomCapacity, section.roomCapacity);
          existing.utilization = calculateUtilization(
            existing.totalMaxEnrollment,
            existing.roomCapacity,
          );
        }
      } else {
        bookings.set(groupKey, {
          key: groupKey,
          termCode: section.termCode,
          campus: meeting.campus || section.campus,
          room: meeting.room,
          dayCode: meeting.dayCode,
          startMinutes: meeting.startMinutes,
          endMinutes: meeting.endMinutes,
          startDate: meeting.startDate,
          endDate: meeting.endDate,
          crossListGroup: section.crossListGroup,
          sectionCrns: [section.crn],
          totalMaxEnrollment: section.maxEnrollment,
          roomCapacity: section.roomCapacity,
          utilization: calculateUtilization(section.maxEnrollment, section.roomCapacity),
        });
      }
    }
  }
  return [...bookings.values()];
}

export function detectConflicts(bookings: Booking[]): Conflict[] {
  const sorted = [...bookings].sort((a, b) => {
    const location = `${a.termCode}|${a.campus}|${a.room}|${a.dayCode}`.localeCompare(
      `${b.termCode}|${b.campus}|${b.room}|${b.dayCode}`,
    );
    return location || a.startMinutes - b.startMinutes;
  });
  const conflicts: Conflict[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index];
    for (let nextIndex = index + 1; nextIndex < sorted.length; nextIndex += 1) {
      const candidate = sorted[nextIndex];
      if (
        current.termCode !== candidate.termCode ||
        current.campus !== candidate.campus ||
        current.room !== candidate.room ||
        current.dayCode !== candidate.dayCode
      ) {
        break;
      }
      if (candidate.startMinutes >= current.endMinutes) break;
      if (
        current.crossListGroup &&
        current.crossListGroup === candidate.crossListGroup
      ) {
        continue;
      }
      if (
        candidate.startMinutes < current.endMinutes &&
        current.startMinutes < candidate.endMinutes &&
        dateRangesOverlap(
          current.startDate,
          current.endDate,
          candidate.startDate,
          candidate.endDate,
        )
      ) {
        conflicts.push({
          bookingA: current,
          bookingB: candidate,
          overlapStart: Math.max(current.startMinutes, candidate.startMinutes),
          overlapEnd: Math.min(current.endMinutes, candidate.endMinutes),
        });
      }
    }
  }
  return conflicts;
}

function meetingFingerprint(meetings: Meeting[]): string {
  return meetings
    .map(
      (meeting) =>
        `${meeting.dayCode}|${meeting.startMinutes}|${meeting.endMinutes}|${meeting.campus}|${meeting.room}|${meeting.startDate ?? ""}|${meeting.endDate ?? ""}`,
    )
    .sort()
    .join(";");
}

export function compareSections(
  beforeSections: NormalizedSection[],
  afterSections: NormalizedSection[],
): SectionDelta[] {
  const before = new Map(beforeSections.map((section) => [section.crn, section]));
  const after = new Map(afterSections.map((section) => [section.crn, section]));
  const allCrns = [...new Set([...before.keys(), ...after.keys()])].sort();
  const fields: Array<keyof NormalizedSection> = [
    "departmentCode",
    "subject",
    "courseNumber",
    "sectionTitle",
    "sectionCode",
    "maxEnrollment",
    "actualEnrollment",
    "instructionMethod",
    "campus",
    "crossListGroup",
    "roomCapacity",
    "waitlistMax",
    "waitlistEnrollment",
    "facultyName",
  ];
  return allCrns.map((crn) => {
    const previous = before.get(crn) ?? null;
    const current = after.get(crn) ?? null;
    if (!previous) {
      return { crn, kind: "added", before: null, after: current, changedFields: [] };
    }
    if (!current) {
      return { crn, kind: "removed", before: previous, after: null, changedFields: [] };
    }
    const changedFields = fields
      .filter((field) => previous[field] !== current[field])
      .map(String);
    if (meetingFingerprint(previous.meetings) !== meetingFingerprint(current.meetings)) {
      changedFields.push("meetings");
    }
    return {
      crn,
      kind: changedFields.length > 0 ? "changed" : "unchanged",
      before: previous,
      after: current,
      changedFields,
    };
  });
}

export function formatMinutes(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
